import React, { useState } from "react";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { Space, Row, Form, Badge, TextArea } from "@masterteam/mt-design-react";

import Upload from "../../Upload";

import useIsAr from "../../../Hooks/useIsAr";
import { addToast } from "../../../helpers";
import api from "../../../providers/api";

import styles from "./style.module.scss";

const { Field } = Form;

function UpdateForm({
  item,
  index,
  isSemiAuto,
  isPercent,
  error,
  setFieldValue,
  values,
  hasNoBaseline,
  kpi,
  isReadOnly,
}) {
  const [isAr] = useIsAr();
  const [calculating, setCalculating] = useState(false);

  const intl = useIntl();

  const calculateTarget = async (parameters, measureIndex) => {
    if (
      parameters.reduce(
        (acc, param) => acc && !isNaN(parseFloat(param.Value)),
        true
      )
    ) {
      setCalculating(true);

      try {
        const res = await api.kpi.calculateUpdateValue(parameters);
        setFieldValue(`KPIMeasures[${measureIndex}].Value`, res.Data);
      } catch (error) {
        addToast.error(error?.message, {
          toastId: "update-error",
        });
      } finally {
        setCalculating(false);
      }
    }
  };

  const getValidStatusUpdate = async (item, measureIndex) => {
    try {
      const res = await api.kpi.getValidStatusUpdate(kpi?.ID, item?.ID, {
        Value: item?.Value,
      });

      setFieldValue(`KPIMeasures[${measureIndex}].IsMandatory`, !res.Data);
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "update-error",
      });
    }
  };

  return (
    <Space direction="vertical" size="medium" className={styles.measureItem}>
      <Row size="large">
        <Field
          disabled={true}
          label={intl.formatMessage({
            id: "update-kpi.target",
          })}
          hideErrorMessage
          inputProps={{
            value: item?.Target
              ? `${item?.Target}${item?.MaxTarget ? " - " : " "}${
                  item?.MaxTarget ? item?.MaxTarget : ""
                }${
                  isPercent
                    ? " %"
                    : isAr
                    ? kpi?.ArabicUnitDetails
                    : kpi?.EnglishUnitDetails
                }`
              : "",
          }}
        />

        <Field
          disabled={true}
          label={intl.formatMessage({
            id: "common.due-date",
          })}
          hideErrorMessage
          inputProps={{
            value: moment(item.DueDate)
              .locale(isAr ? "ar" : "en")
              .format(isAr ? "YYYY/MM/DD" : "DD/MM/YYYY"),
          }}
        />

        <Field
          type="checkbox"
          disabled={
            isReadOnly || (item?.Status !== "NUV" && item?.Status !== "NA")
          }
          name={`KPIMeasures[${index}].IsSkipped`}
          label={intl.formatMessage({
            id: "common.skip",
          })}
          inputProps={{
            highContrast: true,
            onChange: (val) => {
              setFieldValue(
                `KPIMeasures[${index}].IsSkipped`,
                !item?.IsSkipped
              );
            },
          }}
          hideErrorMessage
        />
      </Row>

      {kpi?.DataSource === "semi-auto" && (
        <Row justify="between" size="medium">
          <div>
            <span>
              <FormattedMessage id="kpi-info.calculation-formula" />
            </span>
            : {kpi?.Formula}
          </div>
        </Row>
      )}

      <Space size="medium" direction="vertical">
        {isSemiAuto ? (
          item.Parameters?.map((param, paramIndex) =>
            item.Parameters[paramIndex]?.IsConstant ? null : (
              <Field
                key={param.ID ?? paramIndex}
                type="number"
                label={param.ParameterName}
                name={`KPIMeasures[${index}].Parameters[${paramIndex}].Value`}
                disabled={
                  item.Parameters[paramIndex]?.IsConstant ||
                  isReadOnly ||
                  item?.IsSkipped
                }
                hideErrorMessage
                inputProps={{
                  value: item.Parameters[paramIndex]?.IsConstant
                    ? item.Parameters[paramIndex]?.ConstantValue
                    : item.Parameters[paramIndex]?.Value,
                  onKeyDown: (evt) => {
                    if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                      evt.preventDefault();
                    }
                  },
                  min: 0,
                  step: "any",
                  error:
                    error?.Parameters?.[paramIndex]?.Value && !item?.IsSkipped,
                  onBlur: () => calculateTarget(item.Parameters, index),
                  onChange: (event) => {
                    setFieldValue(
                      `KPIMeasures[${index}].Parameters[${paramIndex}].Value`,
                      event.target.value
                    );
                  },
                }}
              />
            )
          )
        ) : (
          <Field
            type="number"
            label={
              <FormattedMessage
                id="kpi-info.kpi-value"
                defaultMessage="kpi-info.kpi-value"
              />
            }
            name={`KPIMeasures[${index}].Value`}
            disabled={isReadOnly || item?.IsSkipped}
            hideErrorMessage
            inputProps={{
              value: item.Value,
              onKeyDown: (evt) => {
                if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                  evt.preventDefault();
                }
              },
              min: 0,
              step: "any",
              error: error?.Value && !item?.IsSkipped,
              onBlur: () => getValidStatusUpdate(item, index),
              onChange: (event) => {
                const value = event.target.value;
                setFieldValue(`KPIMeasures[${index}].Value`, value);
                setFieldValue(
                  `KPIMeasures[${index}].IsRequiredValue`,
                  value ? true : false
                );

                if (hasNoBaseline) {
                  values.KPIMeasures.forEach((item, itemIndex) => {
                    if (itemIndex > index) {
                      setFieldValue(`KPIMeasures[${itemIndex}]`, {
                        ...item,
                        IsRequiredValue: value ? true : false,
                      });
                    }
                  });
                }
              },
            }}
          />
        )}
        {isSemiAuto && (
          <div
            onClick={() => {
              calculateTarget(item.Parameters, index);
            }}
          >
            <Badge
              fill="outline"
              loading={calculating}
              variant={error?.Value ? "error" : "primary"}
              size="large"
              block
            >
              <FormattedMessage
                id="kpi-info.kpi-value"
                defaultMessage="kpi-info.kpi-value"
              />
              {item.Value && (
                <strong>
                  {" "}
                  {item.Value?.toFixed(2)}
                  {isPercent
                    ? " %"
                    : isAr
                    ? kpi.ArabicUnitDetails
                    : kpi.EnglishUnitDetails}
                </strong>
              )}
            </Badge>
          </div>
        )}
        <Space direction="vertical">
          <label>
            <FormattedMessage id="kpi-details.justifcations" />
          </label>
          <TextArea
            placeholder={intl.formatMessage({
              id: "kpi-details.justifcations-placeholder",
            })}
            value={item?.Justification}
            disabled={isReadOnly || item?.IsSkipped}
            onChange={(e) =>
              setFieldValue(
                `KPIMeasures[${index}].Justification`,
                e.currentTarget.value
              )
            }
            error={
              item?.IsMandatory && !item?.IsSkipped
                ? !item?.Justification?.trim()?.length
                : false
            }
          />
        </Space>

        <Space direction="vertical">
          <label>
            <FormattedMessage id="kpi-details.corrective-action" />
          </label>
          <TextArea
            placeholder={intl.formatMessage({
              id: "kpi-details.corrective-action-placeholder",
            })}
            value={item?.CorrectiveAction}
            disabled={isReadOnly || item?.IsSkipped}
            onChange={(e) =>
              setFieldValue(
                `KPIMeasures[${index}].CorrectiveAction`,
                e.currentTarget.value
              )
            }
            error={
              item?.IsMandatory && !item?.IsSkipped
                ? !item?.CorrectiveAction?.trim()?.length
                : false
            }
          />
        </Space>

        <Space direction="vertical">
          <label>
            <FormattedMessage id="kpi-details.more-information" />
          </label>
          <TextArea
            placeholder={intl.formatMessage({
              id: "kpi-details.more-information-placeholder",
            })}
            value={item?.MoreInformation}
            disabled={isReadOnly || item?.IsSkipped}
            error={!item?.MoreInformation?.trim()?.length && !item?.IsSkipped}
            onChange={(e) =>
              setFieldValue(
                `KPIMeasures[${index}].MoreInformation`,
                e.currentTarget.value
              )
            }
          />
        </Space>

        <Upload
          name={`KPIMeasures[${index}].Attachments`}
          error={error?.Attachments}
          label={<FormattedMessage id="common.attachment" />}
          onChange={(files) => {
            setFieldValue(`KPIMeasures[${index}].Attachments`, files);
          }}
          multiple
          disabled={isReadOnly || item?.IsSkipped}
          onDelete={(files) =>
            setFieldValue(`KPIMeasures[${index}].Attachments`, files)
          }
          attachments={item.Attachments}
        />
      </Space>
    </Space>
  );
}

export default UpdateForm;
